import React, { useState, useCallback } from 'react'
import Box from '@material-ui/core/Box'
import Paper from '@material-ui/core/Paper'
import useT from '../../../i18ns/admin/useT'
import useGlobalDialog from '../../../providers/admin/dialog/useGlobalDialog'
import { createGlobalDialogConfig } from '../../../utils/default/DialogHelper'
import PlayerInputButton from './PlayerInputButton'
import useGDK from '../../../providers/admin/gdk/useGDK'
import TKeyType from '../../../i18ns/admin/TKeyType'
import { AdminPlayerInfo, PermissionType, PlayerInfoMemoType } from '@golden/gdk-admin'
import { makeStyles } from '@material-ui/core'
import { useChecker } from '../../../utils/admin/AdminRouteHook'
import {
  defaultEditorState,
  EditorState,
  isEmpty,
  parseContent,
  trimEndContent
} from '@golden/tiptap-react'
import { useAsyncEffect } from '../../../utils/default/useAsyncEffect'
import RedTextBubbleEditor from '../../default/form/textEditor/RedTextBubbleEditor'

const useStyles = makeStyles((theme) => ({
  editor: {
    marginTop: theme.spacing(2),
    height: 270,
    boxSizing: 'border-box',
    overflow: 'hidden'
  },
  editorContent: {
    height: '100%',
    overflowX: 'auto',
    overflowY: 'auto',
    '& .ProseMirror': {
      minHeight: '100%',
      boxSizing: 'border-box'
    }
  }
}))

interface PropTypes {
  id: number
  res: AdminPlayerInfo
  type: PlayerInfoMemoType
  disabled?: boolean
  reload: () => void
}

interface PlayerMemoEditorProps {
  value: EditorState
  disabled: boolean
  onChange: (value: EditorState) => void
}

const PlayerMemoEditor: React.FC<PlayerMemoEditorProps> = (props) => {
  const { value, disabled, onChange } = props
  const classes = useStyles()

  return (
    <Paper
      variant="outlined"
      className={classes.editor}
      style={{ border: disabled ? '1px solid rgba(0, 0, 0, 0.26)' : '2px solid #fb9678' }}
    >
      <RedTextBubbleEditor
        value={value}
        disabled={disabled}
        onChange={onChange}
        editorContentClassName={classes.editorContent}
      />
    </Paper>
  )
}

const typeMapLabel: { [key in PlayerInfoMemoType]: TKeyType } = {
  rc: 'common.riskMemo',
  cs: 'common.battleFieldMemo',
  fm: 'common.financeMemo'
}
const typeMapPermission: { [key in PlayerInfoMemoType]: PermissionType } = {
  rc: PermissionType.PLAYER_MANAGEMENT_INFO_RC_MEMO,
  cs: PermissionType.PLAYER_MANAGEMENT_INFO_CS_MEMO,
  fm: PermissionType.PLAYER_MANAGEMENT_INFO_FM_MEMO
}
const typeMapMemo = {
  rc: 'risk_memo',
  cs: 'battle_field_memo',
  fm: 'finance_memo'
} as const

const PlayerMemoInput: React.FC<PropTypes> = (props) => {
  const { id, res, type, reload, disabled: buttonDisabled = false } = props
  const gdk = useGDK()
  const globalDialog = useGlobalDialog()
  const writable = useChecker([typeMapPermission[type]])
  const [value, setValue] = useState<EditorState>(defaultEditorState)
  const [disabled, setDisabled] = useState<boolean>(true)
  const { t } = useT()
  const memo = res[typeMapMemo[type]]

  const handleUpdate = useCallback(() => { setDisabled(false) }, [])
  const handleSave = useCallback(() => {
    const content = trimEndContent(value.content)
    const memo = isEmpty(content) ? '' : JSON.stringify(content)
    gdk.player.updateInfoMemo(id, { type, memo }).subscribe({
      next: () => {
        setDisabled(true)
        reload()
      },
      error: (error) => {
        globalDialog.setConfig(createGlobalDialogConfig({
          showIcon: true,
          showCancel: false,
          variant: 'error',
          message: error.message
        }))
        globalDialog.setOpen({
          id: 'setNicknameFail',
          value: true,
          isOK: false
        })
      }
    })
  }, [gdk, globalDialog, id, reload, type, value.content])

  useAsyncEffect(async (mounted) => {
    const content = await parseContent(memo)
    if (!mounted) return
    setValue({
      ...defaultEditorState,
      content
    })
  }, [memo])

  return (<>
    <Box display="flex" justifyContent="space-between" alignItems="center">
      <Box height={32} display="flex" alignItems="center">{t(typeMapLabel[type])}</Box>
      { writable && (<PlayerInputButton
        title={disabled ? t('common.update') : t('common.save')}
        onClick={disabled ? handleUpdate : handleSave}
        disabled={buttonDisabled}
        isAdornment={false}
      />) }
    </Box>
    <PlayerMemoEditor
      value={value}
      disabled={disabled}
      onChange={setValue}
    />
  </>)
}

export default React.memo(PlayerMemoInput)
